import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { withStyles, makeStyles } from 'tss-react/mui';
import { Box, Dialog, IconButton, InputAdornment, Paper } from '@mui/material';
import {
  ArrowBack, Search, Close,
} from '@mui/icons-material';
import { providerPatientRoute } from '@uplift/constants';
import { patientsAutocompleteRenderItem, StyledClientsSearchInputBase, usePatientsSearch, UserSearch } from '../../PatientSearch';
import { AsyncList } from '../../../../AsyncList';
import { useDebouncedValue } from '../../../../Inputs';
import { Navigation, NavigationSection } from '../../../components';


const usePatientsAsyncListStyles = makeStyles()({
  root: {
    marginTop: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    maxHeight: '220px',
    overflowY: 'auto',
  }
});


const PatientsAsyncList: React.FC<{
  term: string;
  open?: boolean;
  loading?: boolean;
  onSelect: (patient: UserSearch) => void;
}> = ({ term, loading, onSelect, open }) => {
  const { classes } = usePatientsAsyncListStyles();
  const searchPatients = usePatientsSearch();
  const { data, isLoading } = useQuery([ 'patients-search', term ], () => {
    if (!term) {
      return [];
    }

    return searchPatients(term);
  });

  if (!open) {
    return null;
  }

  return (
    <Paper className={classes.root}>
      <AsyncList
        setValue={onSelect}
        loading={loading || isLoading}
        options={data ?? []}
        renderItem={opt => patientsAutocompleteRenderItem(opt, { inputValue: term, selected: false })}
      />
    </Paper>
  );
};


const SearchDialog = withStyles(Dialog, {
  container: {
    alignItems: 'flex-start',
  },
  paper: {
    overflow: 'initial',
    height: 'auto',
  }
});


export type MobilePatientsSearchDialogProps = {
  open: boolean;
  onClose: () => void;
};


export const MobilePatientsSearchDialog: React.FC<MobilePatientsSearchDialogProps> = ({
  open,
  onClose,
}) => {
  const history = useHistory();
  const [ searchValue, setSearchValue ] = React.useState('');
  const [ debouncedSearchValue, inProgress ] = useDebouncedValue(searchValue, 400);

  return (
    <SearchDialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionProps={{
        onExited: () => {
          setSearchValue('');
        }
      }}
    >
      <Navigation position="sticky" variant="clear">
        <NavigationSection alignment="center" vertical="center">
          <IconButton>
            <ArrowBack onClick={onClose} />
          </IconButton>
          <Box width="100%">
            <StyledClientsSearchInputBase
              autoFocus
              fullWidth
              endAdornment={searchValue && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchValue('')}>
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )}
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder="Search clients"
              startAdornment={<InputAdornment position="start"><Search color="disabled" /></InputAdornment>}
            />
          </Box>
        </NavigationSection>
        <Box
          position="absolute"
          width="100%"
          height="0px"
          left="0"
          top="100%"
        >
          <PatientsAsyncList
            term={debouncedSearchValue}
            loading={inProgress}
            open={!!searchValue}
            onSelect={(patient) => {
              onClose();
              history.push(providerPatientRoute.createLink({ patientId: patient.id }));
            }}
          />
        </Box>
      </Navigation>
    </SearchDialog>
  );
};
